import { useState } from "react";
import { CenturionBottomNav, CenturionDialogBanner } from "@/components/centurion/CenturionShell";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SetupForm, CheckinForm, Lessons } from "@/components/money/MoneyFoundationCard";
import { NumeralCard, NumeralRow, DashedBox, roman, HallBanner } from "@/components/centurion/NumeralCard";
import { BackCoin } from "@/components/centurion/ThemedPageHeader";
import icCoins from "@/assets/sim/icon-coins.png";
import { Check, Circle, AlertTriangle } from "lucide-react";
import { useMoneyFoundation } from "@/hooks/useMoneyFoundation";
import { PYRAMID, levelRequirements, pyramidStatus, SIM_PYRAMID_LEVEL } from "@/lib/desk/foundations";

const TAG: Record<string, string> = { sow: "Sow", steward: "Steward", wisdom: "Wisdom" };

/** Full Foundation: every level, its habits, and why it matters. */
export default function MoneyPyramidPage() {
  const { stats, habits } = useMoneyFoundation();
  const [panel, setPanel] = useState<null | "setup" | "checkin" | "lessons">(null);
  const [lessons, setLessons] = useState<string[]>([]);
  const status = pyramidStatus(stats);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden pb-28">
      <div className="mx-auto max-w-3xl px-3 sm:px-5 mt-3 space-y-5">
        <div className="-mx-3 sm:-mx-5 space-y-2"><div className="px-3"><BackCoin to="/dashboard" label="Kingdom Hub" /></div><HallBanner title="Foundation" tagline="From survival to legacy" icon={icCoins} /></div>
        {PYRAMID.map((lv) => {
          const r = levelRequirements(lv.n, stats);
          const p = status.perLevel[lv.n - 1];
          const repair = status.repair === lv.n;
          const locked = lv.n > status.working && !repair;
          const all = [...r.build, ...r.keep];
          const met = all.filter((x) => x.met).length;
          const statusText = repair ? "Needs repair" : p.built && p.kept ? "Kept" : lv.n === status.working ? "In progress" : locked ? "Later" : "Open";
          const icon = (x: (typeof all)[number], keep: boolean) =>
            x.met ? <Check className="h-4 w-4 text-gold-dark" /> : repair && keep ? <AlertTriangle className="h-4 w-4 text-destructive" /> : <Circle className="h-4 w-4 text-gold/70" />;
          return (
            <NumeralCard key={lv.n} n={lv.n} title={`Level ${roman(lv.n)} · ${lv.name}`} subtitle={lv.summary}
              status={statusText} progress={all.length ? met / all.length : 0} progressLabel={`${met}/${all.length} habits`}
              tone={repair ? "repair" : lv.n === status.working ? "active" : locked ? "locked" : "default"}>
              {r.build.map((x) => <NumeralRow key={x.key} icon={icon(x, false)} label={x.label} right={x.met ? "1/1" : "0/1"}
                onClick={locked ? undefined : () => { if (x.tag === "wisdom") { setLessons(lv.lessons ?? []); setPanel("lessons"); } else setPanel("setup"); }} />)}
              {r.keep.length > 0 && (
                <DashedBox label="Habit">
                  <ul className="space-y-1.5">
                    {r.keep.map((x) => <li key={x.key} className="flex items-start gap-2">{icon(x, true)}<span>{x.label}</span></li>)}
                  </ul>
                  <div className="mt-2 text-center">
                    <p className="font-serif">Log this week's check-in</p>
                    <button disabled={locked} onClick={() => setPanel("checkin")} className="font-serif text-gold-dark underline underline-offset-2 disabled:opacity-50">Open check-in</button>
                  </div>
                </DashedBox>
              )}
              <p className="text-xs italic text-muted-foreground">{lv.why}</p>
              {lv.n === SIM_PYRAMID_LEVEL && <p className="text-xs font-semibold text-gold-dark">Built and kept → the Simulation opens.</p>}
            </NumeralCard>
          );
        })}
        <p className="text-[11px] text-muted-foreground">Built on Maslow's hierarchy of needs and habit-formation research.</p>
      </div>
      <Dialog open={panel !== null} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <CenturionDialogBanner title={panel === "setup" ? "Set Your Orders" : panel === "checkin" ? "Weekly Check-in" : "Lessons"} tagline="Small steps. Larger tomorrows." />
          {panel === "setup" && <SetupForm habits={habits} onDone={() => setPanel(null)} />}
          {panel === "checkin" && <CheckinForm onDone={() => setPanel(null)} />}
          {panel === "lessons" && <Lessons seatLessons={lessons} done={stats.lessonsDone} />}
        </DialogContent>
      </Dialog>
      <CenturionBottomNav dark />
    </div>
  );
}
